import React, { useState, useMemo } from 'react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend
} from 'recharts';
import Icon from 'components/AppIcon';
import { format, subDays, startOfDay } from 'date-fns';

const RANGE_OPTIONS = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
];

const SEVERITY_COLORS = {
  'CRÍTICO': '#ef4444',
  'ADVERTENCIA': '#f59e0b',
  'INFO': '#3b82f6',
};

function buildChartData(alerts, days) {
  const result = [];
  const now = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const day = startOfDay(subDays(now, i));
    const dayStr = format(day, 'dd/MM');
    const dayAlerts = (alerts || [])?.filter(a => {
      const d = startOfDay(new Date(a?.triggered_at));
      return d?.getTime() === day?.getTime();
    });
    const critico = dayAlerts?.filter(a => a?.severity === 'CRÍTICO')?.length;
    const advertencia = dayAlerts?.filter(a => a?.severity === 'ADVERTENCIA')?.length;
    const info = dayAlerts?.filter(a => a?.severity === 'INFO')?.length;
    const resolved = dayAlerts?.filter(a => a?.status === 'resolved')?.length;
    result?.push({ day: dayStr, CRÍTICO: critico, ADVERTENCIA: advertencia, INFO: info, resueltos: resolved });
  }
  return result;
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-lg p-2.5 shadow-lg">
      <p className="text-xs font-medium text-foreground mb-1.5">{label}</p>
      {payload?.map((p, i) => (
        <p key={i} className="text-xs" style={{ color: p?.color }}>
          {p?.name}: {p?.value}
        </p>
      ))}
    </div>
  );
};

export default function AlertHistoryChart({ alerts, loading }) {
  const [range, setRange] = useState(30);

  const chartData = useMemo(() => buildChartData(alerts, range), [alerts, range]);

  const totalInRange = useMemo(() => {
    return chartData?.reduce((s, d) => s + d?.CRÍTICO + d?.ADVERTENCIA + d?.INFO, 0);
  }, [chartData]);

  return (
    <div className="bg-card border border-border rounded-xl p-4" style={{ boxShadow: 'var(--shadow-sm)' }}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon name="BarChart3" size={16} color="var(--color-accent)" strokeWidth={2} />
          <h2 className="text-sm font-semibold text-foreground">Historial de Alertas</h2>
          <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{totalInRange} total</span>
        </div>
        <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
          {RANGE_OPTIONS?.map(opt => (
            <button
              key={opt?.label}
              onClick={() => setRange(opt?.days)}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                range === opt?.days ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {opt?.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="h-52 bg-muted/40 rounded-lg animate-pulse" />
      ) : (
        <ResponsiveContainer width="100%" height={210}>
          <ComposedChart data={chartData} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis
              dataKey="day"
              tick={{ fontSize: 9, fill: 'var(--color-muted-foreground)', fontFamily: 'JetBrains Mono, monospace' }}
              interval={range === 7 ? 0 : range === 30 ? 4 : 9}
            />
            <YAxis tick={{ fontSize: 9, fill: 'var(--color-muted-foreground)', fontFamily: 'JetBrains Mono, monospace' }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ fontSize: '10px', paddingTop: '8px' }}
            />
            <Bar dataKey="CRÍTICO" stackId="a" fill={SEVERITY_COLORS?.['CRÍTICO']} radius={[0, 0, 0, 0]} />
            <Bar dataKey="ADVERTENCIA" stackId="a" fill={SEVERITY_COLORS?.['ADVERTENCIA']} />
            <Bar dataKey="INFO" stackId="a" fill={SEVERITY_COLORS?.['INFO']} radius={[2, 2, 0, 0]} />
            <Line
              type="monotone"
              dataKey="resueltos"
              name="Resueltos"
              stroke="#059669"
              strokeWidth={2}
              dot={false}
              strokeDasharray="4 2"
            />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
